import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Field, reduxForm } from 'redux-form/dist/redux-form'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import {
  map,
  zipObject,
  isEmpty,
  isFunction,
  partial,
  pull,
  times,
  get,
  forEach,
} from 'lodash-es'
import { intl } from 'lang/intl'
import {
  generateUUID,
  modifyDisabledTextColor,
  reduxFormValidators,
} from '../../../../utils'
import {
  AnimateComponent,
  Button,
  ConditionWrapComponent,
  IconComponent,
  ReduxFormSimpleFieldComponent,
} from '../../../../components'
import withFocus from '../../../withFocus'

import './PollCreateEditFormComponent.scss'

const fieldWithFocus = withFocus(ReduxFormSimpleFieldComponent)
const { required } = reduxFormValidators

// Make sure at least two options have some text
const optionValidator = (_, allValues) => {
  let optionsWithValue = 0
  forEach(allValues, (value, key) => {
    if (key !== 'question' && value !== '') {
      optionsWithValue += 1
    }
  })

  return optionsWithValue > 1
    ? undefined
    : intl.formatMessage({
        defaultMessage: 'At least two options are required',
      })
}

const noEmptyStringValidator = (value) => {
  if (value && value.trim && value.trim() !== '') return undefined
  // NOTE (kasperkronborg): This string isn't used for anything, but is required
  // by `redux-form`, so no need for translation.
  return "Can't have an empty string"
}

const getCancelButton = (onCancel) => (
  <div className="action-button" onClick={() => onCancel()}>
    <FormattedMessage defaultMessage="Cancel" />
  </div>
)

const renderCreateEdit = (opt, mode, children) => {
  const { onCancelPollChanges, renderPollUnitWrapper } = opt

  const title =
    mode === 'edit'
      ? intl.formatMessage({ defaultMessage: 'Edit Poll' })
      : intl.formatMessage({
          defaultMessage: 'New Poll',
        })

  return renderPollUnitWrapper({
    title,
    renderHeaderAction: partial(getCancelButton, onCancelPollChanges),
    children,
  })
}

const getSubmitPromise = (onSubmit, poll, values) =>
  new Promise((resolve, reject) => {
    onSubmit(values, { resolve, reject }, get(poll, 'poll_id'))
  })

const getStyle = (styles, type = 'inputStyle') => {
  const style = get(styles, type)

  return !isEmpty(style) ? { style } : {}
}

const getAddFieldIconConfig = (color) => ({
  name: 'plus',
  color: color.replace('#', ''),
  size: [12, 12],
})

const handleRemoveField = (optionId, setOptionIds, change) => {
  change(optionId, '') // makes the form dirty and reset value
  setOptionIds((prevOptionIds) => [...pull(prevOptionIds, optionId)])
}

const generateIdsForOptions = (poll) => {
  const options = get(poll, 'options', [])
  const count = options.length > 0 ? options.length : 2

  return times(count, () => generateUUID())
}

const getFormInitOptions = (optionIds, poll) => {
  const options = get(poll, 'options', [])
  const optionValues = map(options, ({ option }) => option)

  return zipObject(optionIds, optionValues)
}

// focus the last add option field
const getOptionFiled = (optionId, lastAddedOptionId) => {
  return optionId === lastAddedOptionId
    ? fieldWithFocus
    : ReduxFormSimpleFieldComponent
}

const handleAddOption = (setOptionIds, setLastAddedOptionId) => {
  const newId = generateUUID()
  setLastAddedOptionId(newId)
  setOptionIds((prevOptionIds) => [...prevOptionIds, newId])
}

const getOptionPlaceholder = (index) => {
  let placeholder
  if (index < 2) {
    if (index === 0) {
      placeholder = intl.formatMessage({ defaultMessage: 'First option' })
    } else {
      placeholder = intl.formatMessage({ defaultMessage: 'Second option' })
    }
  }

  return { placeholder }
}

const warn = (value) => {
  return value?.length >= 240
    ? intl.formatMessage({
        defaultMessage: 'Long poll question will be shortened on TV cards',
      })
    : undefined
}

const PollCreateEditFormComponent = (props) => {
  const {
    renderPollUnitWrapper,
    onSubmit,
    handleSubmit,
    styles,
    change,
    iconColor,
    accentColor,
    submitting,
    valid,
    poll,
    dirty,
    initialize,
  } = props

  const { secondaryButtonStyle } = styles
  const isSubmitButtonDisabled = !dirty || !valid || submitting
  const mode = isEmpty(poll) ? 'create' : 'edit'
  const [isInit, setIsInit] = useState(true)
  const [lastAddedOptionId, setLastAddedOptionId] = useState()

  const [optionIds, setOptionIds] = useState(generateIdsForOptions(poll))
  const isAddOptionDisabled = optionIds.length >= 7

  /* eslint-disable react-hooks/exhaustive-deps */
  /* disabled rule because it's not a pure function */
  useEffect(() => {
    setIsInit(false)

    if (mode === 'edit') {
      initialize({
        question: poll.question,
        ...getFormInitOptions(optionIds, poll),
      })
    }
  }, [])
  /* eslint-enable react-hooks/exhaustive-deps */

  const submitButtonText =
    mode === 'edit'
      ? intl.formatMessage({ defaultMessage: 'Save Changes' })
      : intl.formatMessage({
          defaultMessage: 'New Poll',
        })

  return (
    <div className="poll-create-edit-form-component">
      <ConditionWrapComponent
        condition={isFunction(renderPollUnitWrapper)}
        wrap={partial(renderCreateEdit, props, mode)}
      >
        <form
          className="form-wrapper"
          onSubmit={handleSubmit(partial(getSubmitPromise, onSubmit, poll))}
        >
          <Field
            name="question"
            type="textarea"
            validate={required}
            component={fieldWithFocus}
            warn={warn}
            placeholder={intl.formatMessage({
              defaultMessage: 'Type in your question...',
            })}
            customInputProps={getStyle(styles)}
          />
          <AnimateComponent
            duration={300}
            transitionName={isInit ? 'animation-fade' : 'animation-height'}
          >
            {optionIds.map((optionId, index) => (
              <div key={optionId} className="option-field">
                <Field
                  name={optionId}
                  component={getOptionFiled(optionId, lastAddedOptionId)}
                  type="text"
                  forceHideError
                  validate={[optionValidator, noEmptyStringValidator]}
                  customInputProps={getStyle(styles)}
                  {...getOptionPlaceholder(index)}
                />
                <div className="remove-field">
                  {optionIds.length > 2 && (
                    <IconComponent
                      name="cancel"
                      size={[10, 10]}
                      color={iconColor}
                      onClick={partial(
                        handleRemoveField,
                        optionId,
                        setOptionIds,
                        change
                      )}
                    />
                  )}
                </div>
              </div>
            ))}
          </AnimateComponent>
          <Button
            style={{ color: accentColor, background: 'none' }}
            color="transparent"
            iconConfig={getAddFieldIconConfig(accentColor)}
            className={classnames('add-another', {
              disabled: isAddOptionDisabled,
            })}
            disabled={isAddOptionDisabled}
            onClick={partial(
              handleAddOption,
              setOptionIds,
              setLastAddedOptionId
            )}
          >
            <FormattedMessage defaultMessage="Add Another" />
          </Button>
          <div className="submit-button-wrapper">
            <Button
              style={modifyDisabledTextColor(
                secondaryButtonStyle,
                isSubmitButtonDisabled
              )}
              type="submit"
              color="transparent"
              className="create-poll-button"
              disabled={isSubmitButtonDisabled}
              round
            >
              {submitButtonText}
            </Button>
          </div>
        </form>
      </ConditionWrapComponent>
    </div>
  )
}

PollCreateEditFormComponent.propTypes = {
  accentColor: PropTypes.string.isRequired,
  renderPollUnitWrapper: PropTypes.func,
  poll: PropTypes.object,
  onSubmit: PropTypes.func,
  iconColor: PropTypes.string,
  onCancelPollChanges: PropTypes.func,
  styles: PropTypes.shape({
    inputStyle: PropTypes.object,
    buttonStyle: PropTypes.object,
    secondaryButtonStyle: PropTypes.object,
  }),
  // redux-form props
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
  submitting: PropTypes.bool,
  initialize: PropTypes.func,
  valid: PropTypes.bool,
  dirty: PropTypes.bool,
}

PollCreateEditFormComponent.defaultProps = {
  styles: {},
}

export default reduxForm()(PollCreateEditFormComponent)
